import { useState, useCallback, createContext, useContext, ReactNode } from 'react'

interface ToastState {
  type: 'error' | 'success'
  title?: string
  message: string
  isVisible: boolean
}

interface ToastContextType {
  toast: ToastState
  showToast: (type: 'error' | 'success', message: string, title?: string) => void
  showError: (message: string, title?: string) => void
  showSuccess: (message: string, title?: string) => void
  hideToast: () => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState>({
    type: 'error',
    message: '',
    isVisible: false
  })

  const showToast = useCallback((
    type: 'error' | 'success',
    message: string,
    title?: string
  ) => {
    console.log('showToast called:', { type, message, title })
    setToast({
      type,
      title,
      message,
      isVisible: true
    })
  }, [])

  const showError = useCallback((message: string, title?: string) => {
    showToast('error', message, title)
  }, [showToast])

  const showSuccess = useCallback((message: string, title?: string) => {
    showToast('success', message, title)
  }, [showToast])

  const hideToast = useCallback(() => {
    setToast(prev => ({
      ...prev,
      isVisible: false
    }))
  }, [])

  return (
    <ToastContext.Provider value={{
      toast,
      showToast,
      showError,
      showSuccess,
      hideToast
    }}>
      {children}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}